import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  CircularProgress,
  Menu,
  ListItemText,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Visibility,
  Assignment,
  Refresh,
  FolderOpen,
  PersonAdd,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import CaseDialog from '../../components/Cases/CaseDialog';
import CaseIntakeWizard from '../../components/Cases/CaseIntakeWizard';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import ListPagination from '../../components/Common/ListPagination';
import { useAuth } from '../../contexts/AuthContext';
import {
  ASSIGNABLE_USERS_QUERY_KEY,
  asAssignableUsers,
  fetchAssignableUsers,
} from '../../lib/assignableUsers';
import { getAuthToken } from '../../lib/authToken';

interface Case {
  id: string;
  title: string;
  caseNumber: string;
  description: string;
  status: 'open' | 'pending' | 'active' | 'on_hold' | 'closed' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  caseType: string;
  estimatedValue: string;
  filingDate: string | null;
  closingDate: string | null;
  client: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedUser?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

const CasesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const canAssign =
    user?.role === 'admin' || Boolean(user?.permissions?.['cases:assign']);

  const assignedToFromUrl = searchParams.get('assignedTo') || '';

  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    search: '',
    representationStatus: '',
    unassigned: '',
    awaitingSignature: '',
    assignedTo: assignedToFromUrl,
  });
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'create'>('view');
  const [intakeWizardOpen, setIntakeWizardOpen] = useState(false);
  const [assignMenuAnchor, setAssignMenuAnchor] = useState<null | HTMLElement>(null);
  const [assignCaseId, setAssignCaseId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  React.useEffect(() => {
    const fromUrl = searchParams.get('assignedTo') || '';
    setFilters((prev) =>
      prev.assignedTo === fromUrl ? prev : { ...prev, assignedTo: fromUrl }
    );
  }, [searchParams]);

  React.useEffect(() => {
    setPage(1);
  }, [filters]);

  // Fetch cases (filters wired to API) — tenantId in key blocks cross-tenant cache reuse
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['cases', user?.tenantId, filters, page, limit],
    queryFn: async () => {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.set('page', String(page));
      params.set('limit', String(limit));

      const response = await fetch(`/api/cases?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch cases');
      }
      
      const result = await response.json();
      return result;
    },
    enabled: Boolean(user?.tenantId),
  });

  const { data: usersData } = useQuery({
    queryKey: [...ASSIGNABLE_USERS_QUERY_KEY, user?.tenantId],
    enabled: canAssign && Boolean(assignMenuAnchor) && Boolean(user?.tenantId),
    queryFn: fetchAssignableUsers,
  });

  const assignUsers = asAssignableUsers(usersData);

  const assignMutation = useMutation({
    mutationFn: async ({ caseId, assignedTo }: { caseId: string; assignedTo: string }) => {
      const res = await fetch(`/api/cases/${caseId}/assign`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assignedTo }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.message || 'Failed to assign case');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      toast.success('Case assigned');
      setAssignMenuAnchor(null);
      setAssignCaseId(null);
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to assign case'),
  });

  // Delete case mutation
  const deleteMutation = useMutation({
    mutationFn: async (caseId: string) => {
      const token = getAuthToken();
      const response = await fetch(`/api/cases/${caseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to delete case');
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success('Case deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleDelete = (caseId: string) => {
    if (window.confirm('Are you sure you want to delete this case?')) {
      deleteMutation.mutate(caseId);
    }
  };

  const handleCreateCase = () => {
    setIntakeWizardOpen(true);
  };

  const handleViewCase = (caseItem: Case) => {
    setSelectedCase(caseItem);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleOpenCaseFolder = (caseItem: Case) => {
    navigate(`/cases/${caseItem.id}/folder`);
  };

  const handleEditCase = (caseItem: Case) => {
    setSelectedCase(caseItem);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'on_hold':
        return 'info';
      case 'closed':
        return 'default';
      case 'archived':
        return 'error';
      default:
        return 'primary';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numAmount || 0);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };



  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    console.error('Cases error:', error);
  }

  // Handle case where data is not available - still show header with Create button
  const cases = data?.data?.cases || [];
  const casesExist = cases.length > 0;
  const casesTotal = data?.data?.pagination?.total || cases.length;

  return (
    <Box>
      {error && (
        <ListLoadErrorAlert
          resourceLabel="cases"
          onRetry={() => refetch()}
        />
      )}
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Cases
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateCase}
            >
              New Case Intake
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage your legal cases and track their progress.
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search cases..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="on_hold">On Hold</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filters.priority}
                  onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="">All Priorities</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Intake</InputLabel>
                <Select
                  value={filters.representationStatus}
                  onChange={(e) =>
                    setFilters({ ...filters, representationStatus: e.target.value })
                  }
                  label="Intake"
                >
                  <MenuItem value="">All representation</MenuItem>
                  <MenuItem value="prospective">Prospective (new intake)</MenuItem>
                  <MenuItem value="engaged">Engaged</MenuItem>
                  <MenuItem value="declined">Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Assignment</InputLabel>
                <Select
                  value={filters.unassigned}
                  onChange={(e) => setFilters({ ...filters, unassigned: e.target.value })}
                  label="Assignment"
                >
                  <MenuItem value="">All assignments</MenuItem>
                  <MenuItem value="true">Unassigned only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setFilters({
                    status: '',
                    priority: '',
                    search: '',
                    representationStatus: '',
                    unassigned: '',
                    awaitingSignature: '',
                    assignedTo: '',
                  });
                  refetch();
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label="New intake queue"
              color={filters.representationStatus === 'prospective' ? 'primary' : 'default'}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  representationStatus: f.representationStatus === 'prospective' ? '' : 'prospective',
                }))
              }
              clickable
            />
            <Chip
              label="Unassigned"
              color={filters.unassigned === 'true' ? 'primary' : 'default'}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  unassigned: f.unassigned === 'true' ? '' : 'true',
                }))
              }
              clickable
            />
            <Chip
              label="Awaiting signature"
              color={filters.awaitingSignature === 'true' ? 'primary' : 'default'}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  awaitingSignature: f.awaitingSignature === 'true' ? '' : 'true',
                }))
              }
              clickable
            />
          </Box>
        </CardContent>
      </Card>

      {/* Cases Table */}
      <Card>
        <CardContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : !casesExist ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No cases found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first case to get started managing your legal matters.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateCase}
                size="large"
              >
                Create Your First Case
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {cases.filter((caseItem: Case) => caseItem.title !== "Unassigned Documents").length} cases
                </Typography>
              </Box>
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Case</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Filing Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cases
                    .filter((caseItem: Case) => caseItem.title !== "Unassigned Documents")
                    .map((caseItem: Case) => {
                      console.log('🎯 Rendering case:', caseItem.title, caseItem.caseNumber);
                      return (
                        <TableRow key={caseItem.id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {caseItem.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {caseItem.caseNumber}
                              </Typography>
                            </Box>
                          </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                            {caseItem.client?.firstName?.charAt(0) || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">
                              {caseItem.client ? `${caseItem.client.firstName} ${caseItem.client.lastName}` : 'No Client'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {caseItem.client?.email || 'No email'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={caseItem.status.replace('_', ' ')}
                          color={getStatusColor(caseItem.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={caseItem.priority}
                          color={getPriorityColor(caseItem.priority) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {caseItem.assignedUser ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                              {caseItem.assignedUser.firstName?.charAt(0) || '?'}
                            </Avatar>
                            <Typography variant="body2">
                              {caseItem.assignedUser.firstName} {caseItem.assignedUser.lastName}
                            </Typography>
                          </Box>
                        ) : canAssign ? (
                          <Button
                            size="small"
                            startIcon={<PersonAdd fontSize="small" />}
                            onClick={(e) => {
                              setAssignCaseId(caseItem.id);
                              setAssignMenuAnchor(e.currentTarget);
                            }}
                          >
                            Assign
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(caseItem.estimatedValue)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {caseItem.filingDate ? (
                          <Typography variant="body2">
                            {formatDate(caseItem.filingDate)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No filing date
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleViewCase(caseItem)}
                          title="View Case"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCaseFolder(caseItem)}
                          title="Open Case Folder"
                        >
                          <FolderOpen />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleEditCase(caseItem)}
                          title="Edit Case"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(caseItem.id)}
                          color="error"
                          title="Delete Case"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                </TableBody>
              </Table>
            </TableContainer>
            <ListPagination
              page={page}
              limit={limit}
              total={casesTotal}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
            </>
          )}
        </CardContent>
      </Card>

      {/* Case Dialog */}
      <CaseDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        caseData={selectedCase}
        mode={dialogMode}
      />

      <CaseIntakeWizard
        open={intakeWizardOpen}
        onClose={() => setIntakeWizardOpen(false)}
        onCreated={(caseId) => {
          queryClient.invalidateQueries({ queryKey: ['cases'] });
          if (caseId) navigate(`/cases/${caseId}/folder`);
        }}
      />

      <Menu
        anchorEl={assignMenuAnchor}
        open={Boolean(assignMenuAnchor)}
        onClose={() => {
          setAssignMenuAnchor(null);
          setAssignCaseId(null);
        }}
      >
        {assignUsers.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary="Loading users…" />
          </MenuItem>
        ) : (
          assignUsers.map((u: any) => (
            <MenuItem
              key={u.id}
              disabled={assignMutation.isPending}
              onClick={() => {
                if (!assignCaseId) return;
                assignMutation.mutate({ caseId: assignCaseId, assignedTo: u.id });
              }}
            >
              <ListItemText primary={`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} />
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
};

export default CasesPage; 